import styled, { keyframes } from "styled-components";

import { ANIMATION, OVERLAYS, SPACING } from "../../utils/constants";
import { BaseButton } from "../Button";

type ActionVariant = "warn" | "destructive";

const backdropFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const sheetSlideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background: ${OVERLAYS.backdropLight};
  padding: ${SPACING.sm}px;
  padding-bottom: max(${SPACING.sm}px, env(safe-area-inset-bottom));
  animation: ${backdropFadeIn} ${ANIMATION.duration.normal}
    ${ANIMATION.easing.easeOut};
`;

export const SheetGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  width: 100%;
  /* Margin, not justify-content: that clips the top of an over-tall sheet instead of scrolling it. */
  margin-top: auto;
  animation: ${sheetSlideUp} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOutQuint};
`;

export const SheetCard = styled.div`
  background: var(--color-background-2);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

export const SheetHeader = styled.div<{ $column?: boolean }>`
  display: flex;
  flex-direction: ${({ $column }) => ($column ? "column" : "row")};
  align-items: ${({ $column }) => ($column ? "center" : "flex-start")};
  justify-content: ${({ $column }) => ($column ? "center" : "space-between")};
  gap: ${({ $column }) => ($column ? SPACING.xs : SPACING.md)}px;
  padding: ${SPACING.md}px;
  border-bottom: 1px solid var(--color-border);
`;

export const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const HeaderLabel = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

export const HeaderTitle = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--color-text-default);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const HeaderSubtitle = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const actionColor = (variant?: ActionVariant) => {
  if (variant === "destructive") {
    return "var(--color-error)";
  }
  if (variant === "warn") {
    return "var(--color-warning)";
  }
  return "var(--color-text-default)";
};

const actionHoverBg = (variant?: ActionVariant) => {
  if (variant === "destructive") {
    return "var(--color-error-border)";
  }
  if (variant === "warn") {
    return "color-mix(in srgb, var(--color-warning) 14%, transparent)";
  }
  return "var(--color-primary)";
};

const actionHoverColor = (variant?: ActionVariant) => {
  if (variant === "destructive") {
    return "var(--color-error)";
  }
  if (variant === "warn") {
    return "var(--color-warning)";
  }
  return "var(--color-white)";
};

export const ActionButton = styled(BaseButton)<{
  $variant?: ActionVariant;
  $centered?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: ${({ $centered }) => ($centered ? "center" : "flex-start")};
  width: 100%;
  min-height: 56px;
  padding: ${SPACING.md}px;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--color-border);
  cursor: pointer;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  font-weight: 600;
  letter-spacing: -0.005em;
  text-align: ${({ $centered }) => ($centered ? "center" : "left")};
  color: ${({ $variant }) => actionColor($variant)};
  transition: background ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOut};

  &:last-child {
    border-bottom: none;
  }

  &:hover:not(:disabled),
  &:active:not(:disabled) {
    background: ${({ $variant }) => actionHoverBg($variant)};
    color: ${({ $variant }) => actionHoverColor($variant)};
  }
`;

export const CancelButton = styled(BaseButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 56px;
  padding: ${SPACING.md}px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  font-weight: 700;
  color: var(--color-primary);
  transition: background ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOut};

  &:hover,
  &:active {
    background: var(--color-primary);
    color: var(--color-white);
  }
`;
